import { Button, Image, Space, Tooltip, Typography } from 'antd'
import { HelpMessage } from 'utils/constants'
import CIMSBasicFields from 'components/CIMSBasicFields'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import EditSettingsModal from './components/EditSettingsModal'
import { useProjectSettingsContext } from '../../ProjectSettingsContext'
import { pathify } from '@cims-online/shared'
import AnchorButton from 'components/AnchorButton'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { CIMSPage, Permission } from '@cims-online/shared'
import { simpleEditNotification } from 'hooks/notificationHandler'
import ProjectLink from 'components/ProjectLink'
import { useUserDeleted } from 'hooks/useUserDeleted'
import { dateFormatter } from 'utils/dateFormatter'
import { useProjectContext } from 'context/ProjectContext'
import { REACT_APP_BACK_END_URL } from 'config'

const Settings: React.FC = () => {
  const { projectId } = useProjectContext()
  const { metadata, fetching, projectLayer, editMetadata, refetch } =
    useProjectSettingsContext()
  const downloadUrl = `${REACT_APP_BACK_END_URL}${pathify(
    'api',
    'projects',
    projectId,
    'pdf'
  )}`
  const [visible, toggle] = useCIMSModal()
  const help = HelpMessage.Administration.ProjectSettings.Settings
  const canEditSettings = useCheckPermission(Permission.EditProjectSettings)
  const deleted = useUserDeleted()
  return (
    <Space direction="vertical" size="middle" style={{ maxWidth: '90%' }}>
      <EditSettingsModal
        visible={visible}
        logo={metadata?.projectLogoB64 ?? undefined}
        onClose={() => toggle(null)}
      />
      {canEditSettings && (
        <Button type="primary" onClick={() => toggle(null)}>
          Edit Settings
        </Button>
      )}
      <CIMSBasicFields
        loading={fetching}
        fields={[
          {
            label: 'Project Logo',
            help: help.Logo,
            children:
              fetching || metadata?.projectLogoB64 ? (
                <Space size="small" direction="vertical" align="center">
                  <Image
                    src={metadata?.projectLogoB64 ?? undefined}
                    width={200}
                    alt={'Project Logo'}
                    style={{ cursor: 'pointer' }}
                  />
                  <AnchorButton
                    href={downloadUrl}
                    disabled={!metadata?.projectLogoB64}
                    loading={false}
                  >
                    Run Example Report
                  </AnchorButton>
                </Space>
              ) : (
                'Upload Project Logo'
              ),
          },
          {
            type: 'tags',
            label: 'Project Admins',
            help: HelpMessage.Administration.ProjectAdmins,
            tags: metadata.admins.map(
              ({ lastEditedDate, lastEditedUser, user }) => ({
                text: user.username,
                tooltip: `Granted by ${lastEditedUser} on ${dateFormatter.datetime(
                  lastEditedDate
                )}`,
                strikethrough: deleted(user),
              })
            ),
          },
          {
            label: 'CM Project Layer',
            help: help.CMProjectLayer,
            children: (
              <Tooltip title={metadata?.cmProjectLayersId}>
                {projectLayer?.name ?? 'No Project Layer ID'}
              </Tooltip>
            ),
          },
          {
            label: 'Prompt Design Submittal at FDC Early Closure',
            help: help.PromptDesignSubmittal,
            type: 'checkbox',
            checked: metadata?.promptDesignSubmittalAtFDCClosure ?? false,
            disabled: {
              disabled: !useCheckPermission(Permission.EditWorkflowSettings),
              reason: 'missing permission',
            },
            onChange: {
              loadingKey: 'prompt',
              callback: async () => {
                await editMetadata({
                  variables: {
                    editProjectMetadataInput: {
                      projectId,
                      promptDesignSubmittalAtFDCClosure:
                        !metadata?.promptDesignSubmittalAtFDCClosure,
                    },
                  },
                  onCompleted: () => {
                    simpleEditNotification(
                      'Prompt Design Submittal at FDC Closure'
                    )
                    refetch()
                  },
                })
              },
            },
          },
          {
            label: (
              <Typography.Text>
                Replace spaces with dashes in{' '}
                <ProjectLink item={CIMSPage.Filenames}>Filenames</ProjectLink>
              </Typography.Text>
            ),
            help: help.ReplaceSpaces,
            type: 'checkbox',
            checked: metadata?.replaceSpacesWithDashesInFilenames ?? false,
            disabled: {
              disabled: !canEditSettings,
              reason: 'missing permission',
            },
            onChange: {
              loadingKey: 'replace',
              callback: async () => {
                await editMetadata({
                  variables: {
                    editProjectMetadataInput: {
                      projectId,
                      replaceSpacesWithDashesInFilenames:
                        !metadata?.replaceSpacesWithDashesInFilenames,
                    },
                  },
                  onCompleted: () => {
                    simpleEditNotification(
                      'Replace spaces with dashes in Filenames'
                    )
                    refetch()
                  },
                })
              },
            },
          },
        ]}
      />
    </Space>
  )
}

export default Settings
